import React from "react";
import { cn } from "@/lib/utils";

// Sentinelas de highlight. DEVEM casar com as do backend em
// supabase/functions/meilisearch-search/index.ts (HL_PRE/HL_POST). São
// caracteres Private Use Area (U+E000/U+E001): não são HTML e não aparecem
// em dado real, então servem de marcador seguro de "trecho casado".
const HL_PRE = "\uE000";
const HL_POST = "\uE001";

interface Segment {
  text: string;
  mark: boolean;
}

// Quebra a string em segmentos casados / não-casados, sem regex com estado.
// Robusto a sentinelas órfãs (um PRE sem POST vira texto normal).
function parseSegments(input: string): Segment[] {
  const segments: Segment[] = [];
  const pushText = (t: string) => {
    if (t) segments.push({ text: t, mark: false });
  };

  const parts = input.split(HL_PRE);
  // O primeiro pedaço nunca está dentro de um highlight.
  pushText(parts[0]);
  for (let i = 1; i < parts.length; i++) {
    const piece = parts[i];
    const end = piece.indexOf(HL_POST);
    if (end === -1) {
      // Sentinela de abertura sem fechamento: trata como texto comum.
      pushText(piece);
      continue;
    }
    const matched = piece.slice(0, end);
    const rest = piece.slice(end + HL_POST.length);
    if (matched) segments.push({ text: matched, mark: true });
    pushText(rest);
  }
  return segments;
}

interface HighlightedProps {
  text?: string | null;
  className?: string;
  markClassName?: string;
}

/**
 * Renderiza texto que pode conter sentinelas de highlight do Meilisearch,
 * destacando os trechos casados em <mark>.
 *
 * SEGURANÇA (XSS): o conteúdo é dado do usuário. Tudo é renderizado como
 * children JSX — o React escapa automaticamente. NUNCA usar
 * dangerouslySetInnerHTML aqui. Ex.: um título "<img src=x onerror=alert(1)>"
 * é exibido como texto literal, sem criar nenhum nó <img>.
 */
export function Highlighted({ text, className, markClassName }: HighlightedProps) {
  if (!text) return null;
  const segments = parseSegments(text);

  return (
    <span className={className}>
      {segments.map((seg, i) =>
        seg.mark ? (
          <mark
            key={i}
            className={cn(
              "bg-transparent font-semibold text-foreground",
              markClassName,
            )}
          >
            {seg.text}
          </mark>
        ) : (
          <React.Fragment key={i}>{seg.text}</React.Fragment>
        ),
      )}
    </span>
  );
}
